import { useState, useCallback } from 'react'
import apiService from '../../../services/ApiService'

export const useEmailManagement = () => {
  const [templates, setTemplates] = useState([])
  const [emailHistory, setEmailHistory] = useState([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const pageSize = 10

  // Template Management
  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true)
      const response = await apiService.getEmailTemplates()
      setTemplates(Array.isArray(response) ? response : response.data || [])
    } catch (error) {
      console.error('Failed to load templates:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  const createTemplate = useCallback(
    async (templateData) => {
      try {
        const response = await apiService.createEmailTemplate(templateData)
        await loadTemplates() // Reload templates
        return response
      } catch (error) {
        console.error('Failed to create template:', error)
        throw error
      }
    },
    [loadTemplates]
  )

  const updateTemplate = useCallback(
    async (templateId, templateData) => {
      try {
        const response = await apiService.updateEmailTemplate(
          templateId,
          templateData
        )
        await loadTemplates() // Reload templates
        return response
      } catch (error) {
        console.error('Failed to update template:', error)
        throw error
      }
    },
    [loadTemplates]
  )

  const deleteTemplate = useCallback(
    async (templateId) => {
      try {
        await apiService.deleteEmailTemplate(templateId)
        await loadTemplates() // Reload templates
      } catch (error) {
        console.error('Failed to delete template:', error)
        throw error
      }
    },
    [loadTemplates]
  )

  // Email Sending
  const sendEmail = useCallback(async (emailData) => {
    try {
      setLoading(true)
      const response = await apiService.sendEmail(emailData)
      return response
    } catch (error) {
      console.error('Failed to send email:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  // Email History
  const loadEmailHistory = useCallback(
    async (page = currentPage) => {
      try {
        setLoading(true)
        const params = {
          page: page - 1,
          size: pageSize,
        }

        const response = await apiService.getEmailHistory(params)

        if (response.content) {
          setEmailHistory(response.content)
          setTotalPages(response.totalPages || 1)
          setTotalItems(response.totalItems || 0)
          setCurrentPage(page)
        } else if (Array.isArray(response)) {
          setEmailHistory(response)
          setTotalPages(Math.ceil(response.length / pageSize))
          setTotalItems(response.length)
        }
      } catch (error) {
        console.error('Failed to load email history:', error)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [currentPage]
  )

  const getRecipientCount = useCallback(async (recipientType) => {
    try {
      const response = await apiService.getRecipientCount(recipientType)
      return response.count || 0
    } catch (error) {
      console.error('Failed to get recipient count:', error)
      return 0
    }
  }, [])

  const retryFailedEmails = useCallback(
    async (emailHistoryId) => {
      try {
        await apiService.retryFailedEmails(emailHistoryId)
        await loadEmailHistory(currentPage) // Reload history
      } catch (error) {
        console.error('Failed to retry failed emails:', error)
        throw error
      }
    },
    [currentPage, loadEmailHistory]
  )

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page)
      loadEmailHistory(page)
    },
    [loadEmailHistory]
  )

  return {
    // Templates
    templates,
    loadTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,

    // Email Sending
    sendEmail,

    // Email History
    emailHistory,
    loadEmailHistory,
    currentPage,
    totalPages,
    totalItems,
    handlePageChange,

    // Recipients
    getRecipientCount,

    // Retry
    retryFailedEmails,

    // Loading state
    loading,
  }
}
